import type { HealthLevel, PhaseHealth, TaskStatus } from '@/lib/tracker';
import { HEALTH_LABEL, STATUS_LABEL } from '@/lib/tracker';
import { cn, fmtDate, initials } from '@/lib/utils';

export const HEALTH_VAR: Record<HealthLevel, string> = { GREEN: 'var(--ok)', AMBER: 'var(--warn)', RED: 'var(--bad)' };
const HEALTH_SOFT: Record<HealthLevel, string> = {
  GREEN: 'bg-ok-soft text-ok',
  AMBER: 'bg-warn-soft text-warn',
  RED: 'bg-bad-soft text-bad',
};

export function HealthDot({ level, className }: { level: HealthLevel; className?: string }) {
  return (
    <span
      className={cn('inline-block size-2.5 shrink-0 rounded-full', className)}
      style={{ background: HEALTH_VAR[level] }}
      aria-label={HEALTH_LABEL[level]}
      role="img"
    />
  );
}

/** Health with the reasons behind it; an override shows what the arithmetic said. */
export function HealthBadge({ health, compact }: { health: PhaseHealth; compact?: boolean }) {
  const why = health.overridden
    ? `Set to ${HEALTH_LABEL[health.effective]} by BlackWolf: ${health.overrideReason}. Computed: ${HEALTH_LABEL[health.computed]}${health.reasons.length ? ` (${health.reasons.join('; ')})` : ''}.`
    : health.reasons.join('; ') || 'Nothing late.';
  return (
    <span
      title={why}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium',
        HEALTH_SOFT[health.effective],
      )}
    >
      <HealthDot level={health.effective} />
      {!compact && HEALTH_LABEL[health.effective]}
      {health.overridden && <span className="opacity-75">(override)</span>}
    </span>
  );
}

const STATUS_STYLE: Record<TaskStatus, string> = {
  NOT_STARTED: 'bg-surface-2 text-muted',
  IN_PROGRESS: 'bg-info-soft text-info',
  BLOCKED: 'bg-bad-soft text-bad',
  IN_REVIEW: 'bg-warn-soft text-warn',
  DONE: 'bg-ok-soft text-ok',
};

export function StatusPill({ status, className }: { status: TaskStatus; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap',
        STATUS_STYLE[status],
        className,
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

export function Avatar({ name, size = 22 }: { name: string | null | undefined; size?: number }) {
  if (!name) {
    return (
      <span
        className="inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-border-strong text-[9px] text-subtle"
        style={{ width: size, height: size }}
        title="Unassigned"
      >
        ?
      </span>
    );
  }
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-surface-3 font-semibold text-text"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
      title={name}
    >
      {initials(name)}
    </span>
  );
}

export function DueDate({ date, overdue, className }: { date: string | null; overdue?: boolean; className?: string }) {
  if (!date) return <span className={cn('text-subtle', className)}>No date</span>;
  return (
    <span className={cn('tabular', overdue ? 'font-medium text-bad' : 'text-muted', className)}>{fmtDate(date)}</span>
  );
}

export function PhaseChip({ number, name }: { number: number; name?: string }) {
  return (
    <span
      className="inline-flex items-center rounded border border-border bg-surface-2 px-1 text-[10.5px] font-medium tabular text-muted"
      title={name}
    >
      P{number}
    </span>
  );
}

/** Two numbers kept apart: tasks done (ring) and deliverables accepted (text). */
export function ProgressRing({ done, total, size = 44 }: { done: number; total: number; size?: number }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const pct = total ? done / total : 0;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`${done} of ${total} tasks done`}
    >
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-3)" strokeWidth={5} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--brand)"
        strokeWidth={5}
        strokeDasharray={`${c * pct} ${c}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        fontSize={size * 0.26}
        fontWeight={600}
        fill="var(--text)"
      >
        {total ? Math.round(pct * 100) : 0}%
      </text>
    </svg>
  );
}
